import * as React from 'react';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context';

export interface AuthGateProps {
  children: ReactNode;
  /**
   * Force an interactive Keycloak prompt when this app has no local session;
   * keep false for normal console SSO across CGuard SPAs.
   */
  forceLoginPrompt?: boolean;
}

export function AuthGate({ children, forceLoginPrompt = false }: AuthGateProps): React.JSX.Element {
  const { t } = useTranslation(['common']);
  const { status, signIn } = useAuth();
  const redirectStarted = useRef(false);
  const [redirectError, setRedirectError] = React.useState<Error | null>(null);

  // A session that lapsed mid-use (token expired) takes the SSO-friendly path:
  // a plain full-page signinRedirect() silently bounces through a still-valid
  // Keycloak SSO cookie and only shows the login form when the SSO session is
  // genuinely gone. forceLoginPrompt remains available for explicit reauth
  // flows, but normal console entry must preserve cross-console SSO.
  const sessionExpired = status.state === 'unauthenticated' && status.reason === 'expired';
  const explicitlySignedOut = status.state === 'unauthenticated' && status.reason === 'signed-out';

  const startSignIn = React.useCallback(
    (preferInteractivePrompt: boolean): void => {
      redirectStarted.current = true;
      const promptOptions =
        forceLoginPrompt && preferInteractivePrompt
          ? { prompt: 'login' as const, maxAgeSeconds: 0 }
          : undefined;
      void signIn(promptOptions).catch((error: unknown) => {
        redirectStarted.current = false;
        setRedirectError(error instanceof Error ? error : new Error(String(error)));
      });
    },
    [forceLoginPrompt, signIn],
  );

  useEffect(() => {
    if (status.state === 'authenticated') {
      redirectStarted.current = false;
      return;
    }
    if (
      status.state !== 'unauthenticated' ||
      explicitlySignedOut ||
      redirectStarted.current ||
      redirectError
    ) {
      return;
    }

    startSignIn(!sessionExpired);
  }, [explicitlySignedOut, redirectError, sessionExpired, startSignIn, status.state]);

  if (status.state === 'authenticated') {
    return <>{children}</>;
  }

  if (status.state === 'forbidden') {
    return (
      <div role="alert" className="m-auto max-w-md p-6 text-sm">
        <h1 className="mb-2 text-lg font-semibold">Access is not provisioned</h1>
        <p>
          Your identity is signed in, but Central did not return an active authorization binding.
          Contact an administrator before retrying.
        </p>
      </div>
    );
  }

  if (status.state === 'error') {
    return (
      <div role="alert" className="m-auto max-w-md p-6 text-sm">
        <h1 className="mb-2 text-lg font-semibold">Sign-in failed</h1>
        <p>
          The authenticated session could not be verified: {status.error.message}. Reload the page
          to retry, or contact your CGuard administrator.
        </p>
      </div>
    );
  }

  if (redirectError) {
    return (
      <div role="alert" className="m-auto max-w-md p-6 text-sm">
        <h1 className="mb-2 text-lg font-semibold">Sign-in could not start</h1>
        <p>{redirectError.message}</p>
        <button
          type="button"
          className="mt-4 rounded-sm border px-3 py-2"
          onClick={() => {
            redirectStarted.current = false;
            setRedirectError(null);
            startSignIn(!sessionExpired);
          }}
        >
          Try sign-in again
        </button>
      </div>
    );
  }

  if (explicitlySignedOut) {
    const signedOutTitle = t('common:auth.signedOutTitle', { defaultValue: 'Signed out' });
    const signedOutBody = t('common:auth.signedOutBody', {
      defaultValue: 'Your session has ended.',
    });
    const signInLabel = t('common:auth.signIn', { defaultValue: 'Sign in' });
    return (
      <div className="m-auto max-w-md p-6 text-sm">
        <h1 className="mb-2 text-lg font-semibold">{signedOutTitle}</h1>
        <p>{signedOutBody}</p>
        <button
          type="button"
          className="mt-4 rounded-sm border px-3 py-2"
          onClick={() => startSignIn(true)}
        >
          {signInLabel}
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
      {sessionExpired ? 'Session expired. Signing in again...' : 'Signing in...'}
    </div>
  );
}
